type LinkResponse = { links?: Array<{ id: string; link: string }> };

/** Signed media links for a whole page of rows, keyed by `mediaLinkKey`. */
export type MediaLinks = Map<string, string>;

export const mediaLinkKey = (type: number, id: string) => `${type}:${id}`;

/**
 * Resolves the media on a page of rows in one request per (organisation,
 * media type), rather than one or two per row.
 *
 * Every list used to resolve its media inside a per-row map, so a page of N
 * rows opened up to 2N requests before it could render, and the pickers that
 * fetch 1000 staff opened up to 2000. getMediaLink already takes an array of
 * ids, and the rows on a page share one groupId in practice, so the same work
 * collapses into a request or two. This is the pattern contractSlice's
 * fetchContracts has used since that listing was measured.
 *
 * `collect` is called once per row and names the media it wants through
 * `want(groupId, type, id)`. A failed group is logged and simply leaves its
 * rows without links, as the per-row catches did.
 */
export async function resolveMediaLinks<T>(
  rows: T[],
  collect: (
    row: T,
    want: (groupId: string | undefined | null, type: number, id?: string | null) => void,
  ) => void,
  getMediaLink: (ids: string[], type: number, groupId?: string) => Promise<LinkResponse>,
  label = "Media",
): Promise<MediaLinks> {
  /** groupId -> media type -> ids to resolve. */
  const wanted = new Map<string, Map<number, Set<string>>>();
  const want = (
    groupId: string | undefined | null,
    type: number,
    id?: string | null,
  ) => {
    if (!id) return;
    // Rows without a groupId are still resolved, as the per-row calls did.
    const group = groupId ?? "";
    if (!wanted.has(group)) wanted.set(group, new Map());
    const byType = wanted.get(group)!;
    if (!byType.has(type)) byType.set(type, new Set());
    byType.get(type)!.add(id);
  };
  rows.forEach((row) => collect(row, want));

  const links: MediaLinks = new Map();
  await Promise.all(
    [...wanted.entries()].flatMap(([groupId, byType]) =>
      [...byType.entries()].map(async ([type, ids]) => {
        try {
          const res = await getMediaLink([...ids], type, groupId || undefined);
          for (const link of res?.links || []) {
            if (link?.id) links.set(mediaLinkKey(type, link.id), link.link);
          }
        } catch (err) {
          console.error(`${label} media resolution failed:`, err);
        }
      }),
    ),
  );
  return links;
}
